// src/lib/supabaseClient.js
// Robust Supabase client wrapper with timeout, retry, and deduplication

/**
 * Custom error types for better error handling
 */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network request failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Wrap a Promise with a timeout
 * @param {Promise} promise - The promise to wrap (can be a thenable like Supabase query builder)
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise} Promise that rejects if timeout is exceeded
 */
export async function withTimeout(promise, timeoutMs = 10000) {
  // CRITICAL: Explicitly convert thenable to a real Promise to ensure it starts executing
  // Supabase query builders are "thenables" that don't start until .then() is called
  // Promise.resolve() calls .then() internally, which triggers the query execution
  const actualPromise = Promise.resolve(promise);

  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new TimeoutError(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([actualPromise, timeoutPromise]);
}

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {number} maxAttempts - Maximum number of attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Result of the function
 */
export async function withRetry(fn, maxAttempts = 3, baseDelay = 1000) {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error;

      // Don't retry on certain errors
      if (
        error.code === 'PGRST116' || // Not found
        error.message?.includes('JWT') || // Auth errors
        error.message?.includes('unique constraint') || // Duplicate key
        attempt === maxAttempts // Last attempt
      ) {
        throw error;
      }

      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt - 1);
      const jitter = Math.random() * 200; // Add jitter to prevent thundering herd
      const totalDelay = delay + jitter;

      if (import.meta.env.VITE_DEBUG) {
        console.log(`Retry attempt ${attempt}/${maxAttempts} after ${totalDelay.toFixed(0)}ms`);
      }

      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }

  throw lastError;
}

/**
 * In-flight request cache for deduplication
 */
const inFlightRequests = new Map();

/**
 * Deduplicate simultaneous identical requests
 * @param {string} key - Unique key for this request
 * @param {Function} fn - Async function that returns a promise
 * @returns {Promise} Result of the function
 */
export async function deduplicateRequest(key, fn) {
  // If request is already in-flight, return existing promise
  if (inFlightRequests.has(key)) {
    if (import.meta.env.VITE_DEBUG) {
      console.log(`Deduplicating request: ${key}`);
    }
    return inFlightRequests.get(key);
  }

  // Start new request
  const promise = fn()
    .finally(() => {
      // Clean up after request completes
      inFlightRequests.delete(key);
    });

  // Store in-flight request
  inFlightRequests.set(key, promise);

  return promise;
}

/**
 * Wrap a Supabase query with timeout and retry
 * @param {Promise} queryBuilder - Supabase query builder promise
 * @param {Object} options - Options
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {number} options.retries - Max retry attempts (default: 3)
 * @param {string} options.dedupeKey - Key for request deduplication (optional)
 * @returns {Promise} Query result with {data, error}
 */
export async function wrapQuery(queryBuilder, options = {}) {
  const {
    timeout = 10000,
    retries = 3,
    dedupeKey = null
  } = options;

  const executeQuery = async () => {
    try {
      // Execute query with timeout
      const result = await withTimeout(queryBuilder, timeout);

      // Supabase returns {data, error} format
      if (result.error) {
        throw result.error;
      }

      return result;
    } catch (error) {
      // Convert timeout errors
      if (error instanceof TimeoutError) {
        return {
          data: null,
          error: {
            message: error.message,
            code: 'TIMEOUT',
            details: null
          }
        };
      }

      // Re-throw to allow retry logic to handle
      throw error;
    }
  };

  try {
    // If deduplication key is provided, deduplicate the request
    if (dedupeKey) {
      return await deduplicateRequest(dedupeKey, () => withRetry(executeQuery, retries));
    }

    // Otherwise, just retry
    return await withRetry(executeQuery, retries);
  } catch (error) {
    // Return Supabase-style error response
    return {
      data: null,
      error: {
        message: error.message || 'Unknown error',
        code: error.code || 'UNKNOWN',
        details: error.details || null,
        hint: error.hint || null
      }
    };
  }
}

/**
 * Create a wrapped Supabase client that automatically applies timeout and retry
 * Note: We just return the original client since the spread operator doesn't
 * properly copy all methods. Services will use wrapQuery() explicitly.
 */
export function createWrappedClient(supabase) {
  // Just return the original client - wrapQuery will be used explicitly
  return supabase;
}
